import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { X, Upload, RotateCcw, Save, ZoomIn, ZoomOut, Loader, Plus, Trash2, ChevronLeft, ChevronRight, Pencil } from '../lib/icons';
import type { ImageSlot } from '../lib/image-slots-client';
import {
  getGallerySlots,
  nextGallerySlotId,
  createImageSlot,
  deleteImageSlot,
  saveImageSlot,
  uploadImageFile,
  useImageSlots,
  isVideoUrl,
} from '../lib/image-slots-client';

const ACCENT = '#39FF14';

const ASPECT_PRESETS = [
  { label: 'Auto',      value: 'auto',  ratio: 16 / 9 },
  { label: 'Square',    value: '1:1',   ratio: 1 },
  { label: 'Portrait',  value: '3:4',   ratio: 3 / 4 },
  { label: 'Landscape', value: '16:9',  ratio: 16 / 9 },
  { label: 'Wide',      value: '21:9',  ratio: 21 / 9 },
  { label: 'Hero',      value: '16:5',  ratio: 16 / 5 },
];

const CONTAINER_PRESETS = [
  { label: 'Auto',      value: 'auto' },
  { label: 'Square',    value: 'square' },
  { label: 'Portrait',  value: 'portrait' },
  { label: 'Landscape', value: 'landscape' },
  { label: 'Wide',      value: 'wide' },
  { label: 'Hero',      value: 'hero' },
  { label: 'Tall Hero', value: 'tall-hero' },
];

export interface ImageEditorProps {
  slot: ImageSlot;
  onSave: (updates: Partial<Omit<ImageSlot, 'slot_id' | 'updated_at'>>) => Promise<void>;
  onClose: () => void;
  /** Called when the user clicks Edit on a gallery sub-image — swaps the editor to that slot */
  onEditGallerySlot?: (slot: ImageSlot) => void;
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all border"
      style={
        active
          ? { backgroundColor: `${ACCENT}20`, color: ACCENT, borderColor: `${ACCENT}60` }
          : { backgroundColor: 'transparent', color: '#B0B0B0', borderColor: '#374151' }
      }
    >
      {children}
    </button>
  );
}

export default function ImageEditor({ slot, onSave, onClose, onEditGallerySlot }: ImageEditorProps) {
  const [altText, setAltText]       = useState(slot.alt_text);
  const [focalX, setFocalX]         = useState(slot.focal_x);
  const [focalY, setFocalY]         = useState(slot.focal_y);
  const [zoom, setZoom]             = useState(slot.zoom);
  const [aspectRatio, setAspectRatio] = useState(slot.aspect_ratio);
  const [containerSize, setContainerSize] = useState(slot.container_size);
  const [objectFit, setObjectFit]   = useState<'cover' | 'contain'>(slot.object_fit as 'cover' | 'contain');
  const [imageSrc, setImageSrc]         = useState(slot.image_url ?? '');
  const [imageSrcIsVideo, setImageSrcIsVideo] = useState(isVideoUrl(slot.image_url ?? ''));
  const [crop, setCrop]                 = useState({ x: 0, y: 0 });
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState<string | null>(null);

  const { slots: allSlots, loading: galleryLoading, refetch: refetchSlots } = useImageSlots();
  const gallerySlots = getGallerySlots(allSlots, slot.slot_id);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryError, setGalleryError]         = useState<string | null>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const isVideo = imageSrcIsVideo || isVideoUrl(imageSrc);

  const selectedAspect = ASPECT_PRESETS.find(p => p.value === aspectRatio) ?? ASPECT_PRESETS[0];

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    // react-easy-crop onCropComplete gives (croppedArea in %, croppedAreaPixels)
    // We use the percentage area to derive focal point center
    void croppedAreaPixels;
    // Use _croppedArea (percentage) to compute center
    const centerX = _croppedArea.x + _croppedArea.width / 2;
    const centerY = _croppedArea.y + _croppedArea.height / 2;
    setFocalX(Math.round(centerX * 10) / 10);
    setFocalY(Math.round(centerY * 10) / 10);
  }, []);

  const handleGalleryAdd = async (file: File) => {
    setGalleryUploading(true);
    setGalleryError(null);
    try {
      const newSlotId = nextGallerySlotId(allSlots, slot.slot_id);
      const url = await uploadImageFile(newSlotId, file);
      await createImageSlot(newSlotId, url, '', slot.page_section, `${slot.label} — gallery`);
      refetchSlots();
    } catch (err) {
      setGalleryError(err instanceof Error ? err.message : String(err));
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleGalleryRemove = async (gallerySlotId: string) => {
    setGalleryError(null);
    try {
      await deleteImageSlot(gallerySlotId);
      refetchSlots();
    } catch (err) {
      setGalleryError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleGalleryReorder = async (idx: number, dir: 'left' | 'right') => {
    const targetIdx = dir === 'left' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= gallerySlots.length) return;
    const a = gallerySlots[idx];
    const b = gallerySlots[targetIdx];
    setGalleryError(null);
    try {
      await Promise.all([
        saveImageSlot(a.slot_id, { image_url: b.image_url, alt_text: b.alt_text }),
        saveImageSlot(b.slot_id, { image_url: a.image_url, alt_text: a.alt_text }),
      ]);
      refetchSlots();
    } catch (err) {
      setGalleryError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleReset = () => {
    setFocalX(50);
    setFocalY(50);
    setZoom(1);
    setAspectRatio('auto');
    setContainerSize('auto');
    setObjectFit('cover');
    setCrop({ x: 0, y: 0 });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({
        image_url:      imageSrc || null,
        alt_text:       altText,
        focal_x:        focalX,
        focal_y:        focalY,
        zoom,
        aspect_ratio:   aspectRatio,
        container_size: containerSize,
        object_fit:     objectFit,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-[#0D0D0D] border border-gray-800 rounded-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 sticky top-0 bg-[#0D0D0D] z-10">
          <div>
            <h2 className="text-lg font-bold text-[#E0E0E0]">{slot.label}</h2>
            <p className="text-xs text-gray-500 font-mono">{slot.slot_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-[#E0E0E0] transition-colors p-1">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-800">
          {/* Left: Crop preview */}
          <div className="p-6 space-y-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
              {objectFit === 'contain' ? 'Preview' : 'Crop & Zoom'}
            </p>

            {/* Preview area */}
            <div className="relative bg-gray-950 rounded-xl overflow-hidden" style={{ height: 280 }}>
              {imageSrc ? (
                isVideo ? (
                  <video
                    src={imageSrc}
                    autoPlay muted loop playsInline
                    className="w-full h-full object-cover"
                  />
                ) : objectFit === 'contain' ? (
                  <img
                    src={imageSrc}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={selectedAspect.ratio}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    style={{
                      containerStyle: { borderRadius: '0.75rem', background: '#030712' },
                      cropAreaStyle: { border: `2px solid ${ACCENT}`, boxShadow: `0 0 0 9999px rgba(0,0,0,0.5)` },
                    }}
                  />
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-600">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-10 w-10">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 21h18M7.5 10.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                  </svg>
                  <p className="text-sm">No image set</p>
                </div>
              )}
            </div>

            {/* Zoom slider — only useful in crop-to-fill mode */}
            {imageSrc && !isVideo && objectFit === 'cover' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-400">Zoom</label>
                  <span className="text-xs font-mono text-[#39FF14]">{zoom.toFixed(2)}x</span>
                </div>
                <div className="flex items-center gap-3">
                  <ZoomOut className="h-4 w-4 text-gray-500 shrink-0" />
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={e => setZoom(Number(e.target.value))}
                    className="flex-1 accent-[#39FF14]"
                  />
                  <ZoomIn className="h-4 w-4 text-gray-500 shrink-0" />
                </div>
              </div>
            )}

            {/* Upload button */}
            <label className="flex items-center gap-2 cursor-pointer px-4 py-2.5 rounded-lg bg-gray-900 border border-gray-700 hover:border-gray-500 text-sm text-[#E0E0E0] transition-all w-full justify-center">
              <Upload className="h-4 w-4" />
              Upload Image or Video
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                className="sr-only"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImageSrc(URL.createObjectURL(file));
                    setImageSrcIsVideo(file.type.startsWith('video/'));
                  }
                  e.target.value = '';
                }}
              />
            </label>
            {imageSrc && imageSrc.startsWith('blob:') && (
              <p className="text-xs text-yellow-400 text-center">Preview only — save to upload to server</p>
            )}
          </div>

          {/* Right: Settings */}
          <div className="p-6 space-y-6">
            {/* Alt text */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Alt Text</label>
              <input
                type="text"
                value={altText}
                onChange={e => setAltText(e.target.value)}
                placeholder="Describe the image..."
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#39FF14] transition-colors"
              />
            </div>

            {/* Focal point */}
            {!isVideo && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Focal Point</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-500 w-6">X</label>
                    <input
                      type="range" min={0} max={100} step={0.5}
                      value={focalX}
                      onChange={e => setFocalX(Number(e.target.value))}
                      className="flex-1 accent-[#39FF14]"
                    />
                    <span className="text-xs font-mono text-[#39FF14] w-10 text-right">{focalX.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-500 w-6">Y</label>
                    <input
                      type="range" min={0} max={100} step={0.5}
                      value={focalY}
                      onChange={e => setFocalY(Number(e.target.value))}
                      className="flex-1 accent-[#39FF14]"
                    />
                    <span className="text-xs font-mono text-[#39FF14] w-10 text-right">{focalY.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Aspect ratio */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Aspect Ratio</p>
              <div className="flex flex-wrap gap-2">
                {ASPECT_PRESETS.map(p => (
                  <PillButton key={p.value} active={aspectRatio === p.value} onClick={() => setAspectRatio(p.value)}>
                    {p.label}
                  </PillButton>
                ))}
              </div>
            </div>

            {/* Container size */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Container Size</p>
              <div className="flex flex-wrap gap-2">
                {CONTAINER_PRESETS.map(p => (
                  <PillButton key={p.value} active={containerSize === p.value} onClick={() => setContainerSize(p.value)}>
                    {p.label}
                  </PillButton>
                ))}
              </div>
            </div>

            {/* Object fit */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Fit Mode</p>
              <div className="flex gap-2">
                <PillButton active={objectFit === 'cover'} onClick={() => setObjectFit('cover')}>
                  Crop to fill
                </PillButton>
                <PillButton active={objectFit === 'contain'} onClick={() => setObjectFit('contain')}>
                  Show full image
                </PillButton>
              </div>
              {objectFit === 'contain' && (
                <p className="text-xs text-gray-500">Full image shown — letterbox bars fill any unused space.</p>
              )}
            </div>

            {/* Gallery images */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Gallery Photos</p>
                <label className="flex items-center gap-1 text-xs text-[#39FF14] hover:text-white cursor-pointer transition-colors">
                  {galleryUploading ? (
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  Add photo/video
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                    className="sr-only"
                    disabled={galleryUploading}
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) handleGalleryAdd(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>

              {galleryError && (
                <p className="text-xs text-red-400">{galleryError}</p>
              )}

              {galleryLoading ? (
                <p className="text-xs text-gray-600">Loading…</p>
              ) : gallerySlots.length === 0 ? (
                <p className="text-xs text-gray-600">No extra photos yet — click "Add photo/video" to attach more angles.</p>
              ) : (
                <div className="space-y-1.5">
                  {gallerySlots.map((g, idx) => (
                    <div key={g.slot_id} className="flex items-center gap-2 group/gthumb">
                      {/* Thumbnail */}
                      <div className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-700 shrink-0">
                        {g.image_url && (
                          isVideoUrl(g.image_url) ? (
                            <video src={g.image_url} muted playsInline className="w-full h-full object-cover" />
                          ) : (
                            <img src={g.image_url} alt={g.alt_text} className="w-full h-full object-cover" />
                          )
                        )}
                      </div>
                      {/* Controls */}
                      <div className="flex items-center gap-1">
                        {onEditGallerySlot && (
                          <button
                            type="button"
                            onClick={() => onEditGallerySlot(g)}
                            className="p-1 rounded text-gray-500 hover:text-[#39FF14] transition-colors"
                            title="Edit / crop this image"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => handleGalleryReorder(idx, 'left')}
                          className="p-1 rounded text-gray-500 hover:text-[#E0E0E0] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move left"
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          disabled={idx === gallerySlots.length - 1}
                          onClick={() => handleGalleryReorder(idx, 'right')}
                          className="p-1 rounded text-gray-500 hover:text-[#E0E0E0] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          title="Move right"
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleGalleryRemove(g.slot_id)}
                          className="p-1 rounded text-gray-600 hover:text-red-400 transition-colors ml-1"
                          aria-label="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <span className="text-[10px] text-gray-600 truncate font-mono">{g.slot_id.split('__g')[1] ? `#${g.slot_id.split('__g')[1]}` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reset */}
            <button
              onClick={handleReset}
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-[#E0E0E0] transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to defaults
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between gap-4 sticky bottom-0 bg-[#0D0D0D]">
          {error && (
            <p className="text-red-400 text-sm flex-1">{error}</p>
          )}
          {!error && <div className="flex-1" />}
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-[#E0E0E0] border border-gray-700 hover:border-gray-600 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-[#39FF14] text-black hover:bg-[#00BFFF] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            >
              {saving ? (
                <Loader className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
